'use client';

import React, { useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageBackdrop from '@/components/PageBackdrop';

function useScrollReveal() {
  useEffect(() => {
    const elements = document.querySelectorAll('.reveal');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

interface Product {
  id: number;
  category: string;
  tags: string[];
}

const MURDER8_MULTITRACKS_URL = 'https://whop.com/diggityglover/raw-drum-cover-multitracks/';

const liveProduct = {
  category: 'Multi-Tracks',
  title: 'MURDER8 — Raw Drum Cover Multitracks',
  description:
    'Every raw drum track from the Bodysnatcher "MURDER8" cover — kick in/out, snare top, toms, overheads and rooms as unprocessed 24-bit WAV, exactly as tracked. Practise your metal drum mixing on a real deathcore session.',
  tags: ['24-bit WAV', 'Unprocessed', 'Full Performance', 'Labelled Stems'],
  price: '£2.99',
  buyUrl: MURDER8_MULTITRACKS_URL,
};

const products: Product[] = [
  { id: 1, category: 'Sample Packs', tags: ['WAV', '24-bit', 'One-Shots'] },
  { id: 2, category: 'Sample Packs', tags: ['Loops', 'Blast Beats', 'Royalty-Free'] },
  { id: 3, category: 'Stems', tags: ['WAV', '48kHz', 'Multitrack'] },
  { id: 4, category: 'Stems', tags: ['Drums', 'Guitars', 'Vocals'] },
  { id: 5, category: 'Multi-Tracks', tags: ['WAV', 'Mix-Ready', 'Full Session'] },
  { id: 7, category: 'Sheet Music', tags: ['PDF', 'Transcription', 'Annotated'] },
  { id: 8, category: 'Sheet Music', tags: ['PDF', 'Drum Notation', 'Note-for-Note'] },
];

export default function ShopPage() {
  useScrollReveal();

  return (
    <div
      style={{ minHeight: '100vh', background: 'transparent', color: '#ffffff', overflowX: 'hidden' }}
    >
      <PageBackdrop src="/assets/images/studio-1.jpg" />
      <Header />

      {/* Grid Overlay */}
      <div className="grid-overlay">
        <div className="grid-inner">
          <div className="grid-line-v" />
          <div className="grid-line-v hidden md:block" />
          <div className="grid-line-v hidden lg:block" />
          <div className="grid-line-v" />
        </div>
      </div>

      {/* Hero with background image */}
      <section className="pt-32 pb-16 border-b border-primary/10 relative overflow-hidden min-h-[420px] flex items-center">
        {/* Background Image */}
        <div className="absolute inset-0">
          <Image
            src="/assets/images/studio-2.jpg"
            alt="Studio mixing desk and monitors"
            fill
            className="object-cover object-center"
            priority
          />
          {/* Dark overlay for readability */}
          <div className="absolute inset-0 bg-black/70" />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'radial-gradient(ellipse at top center, rgba(204,0,0,0.18) 0%, rgba(139,0,0,0.10) 40%, transparent 70%)',
            }}
          />
          <div
            className="absolute inset-0 pointer-events-none opacity-40"
            style={{
              backgroundImage:
                'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(204,0,0,0.015) 3px, rgba(204,0,0,0.015) 4px)',
            }}
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="anim-in-1">
            <span className="section-label block mb-4">// Shop</span>
            <h1
              className="font-display font-extrabold uppercase tracking-tighter text-fg-base mb-4"
              style={{ fontSize: 'clamp(2.5rem, 7vw, 5.5rem)', lineHeight: '0.95' }}
            >
              Shop the
              <br />
              <span className="text-gradient-red">Catalogue.</span>
            </h1>
            <p className="font-body text-muted text-lg max-w-xl leading-relaxed mt-4">
              Sample packs, stems, multi-tracks and sheet music — built for metal musicians and
              producers. The first drop is live.
            </p>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* ─── LIVE PRODUCT — MURDER8 multitracks ─── */}
            <div className="reveal flex flex-col border-2 border-primary bg-bg-card/40 relative overflow-hidden" style={{ boxShadow: '0 0 24px rgba(255,26,26,0.25)' }}>
              {/* New Drop Banner */}
              <div className="absolute top-3 right-3 z-10">
                <span
                  className="font-mono text-xs tracking-widest uppercase px-3 py-1 border border-primary bg-primary text-black font-bold"
                  style={{ boxShadow: '0 0 12px rgba(255,26,26,0.6)' }}
                >
                  New Drop
                </span>
              </div>

              {/* Cover art */}
              <div className="relative border-b border-primary/30" style={{ aspectRatio: '16/9' }}>
                <Image
                  src="/assets/images/murder8-multitracks-cover.jpg"
                  alt="MURDER8 raw drum multitracks cover art — SJC drum kit mic'd up in the studio"
                  fill
                  className="object-cover"
                />
              </div>

              {/* Card Header */}
              <div className="p-6 border-b border-primary/20">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <span className="font-mono text-xs tracking-widest uppercase px-2 py-1 border border-primary/40 text-primary bg-primary/10">
                    {liveProduct.category}
                  </span>
                </div>
                <h2 className="font-display font-bold text-lg uppercase tracking-tight text-fg-base leading-tight">
                  {liveProduct.title}
                </h2>
              </div>

              {/* Card Body */}
              <div className="p-6 flex flex-col flex-1 gap-5">
                <p className="font-body text-muted text-sm leading-relaxed">
                  {liveProduct.description}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2">
                  {liveProduct.tags.map((tag) => (
                    <span
                      key={tag}
                      className="font-mono text-xs text-muted/80 border border-primary/20 px-2 py-0.5 tracking-wide"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Price + CTA */}
                <div className="mt-auto pt-4 border-t border-primary/20 flex items-center justify-between gap-4">
                  <div>
                    <span className="font-display font-extrabold text-2xl text-primary" style={{ textShadow: '0 0 12px rgba(255,26,26,0.5)' }}>
                      {liveProduct.price}
                    </span>
                  </div>
                  <a
                    href={liveProduct.buyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary px-6 py-2 text-xs whitespace-nowrap"
                  >
                    Buy Now
                  </a>
                </div>
              </div>
            </div>

            {products.map((product, i) => (
              <div
                key={product.id}
                className="reveal flex flex-col border border-white/5 bg-bg-card/10 opacity-60 relative overflow-hidden"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                {/* Coming Soon Banner */}
                <div className="absolute top-3 right-3 z-10">
                  <span className="font-mono text-xs tracking-widest uppercase px-3 py-1 border border-white/20 bg-black/60 text-white/50">
                    Coming Soon
                  </span>
                </div>

                {/* Card Header */}
                <div className="p-6 border-b border-white/5">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <span className="font-mono text-xs tracking-widest uppercase px-2 py-1 border border-primary/30 text-primary bg-primary/5">
                      {product.category}
                    </span>
                  </div>
                  <h2 className="font-display font-bold text-lg uppercase tracking-tight text-white/30 leading-tight blur-sm">
                    Untitled Release
                  </h2>
                </div>

                {/* Card Body */}
                <div className="p-6 flex flex-col flex-1 gap-5">
                  <p className="font-body text-white/20 text-sm leading-relaxed blur-sm">
                    Full details dropping soon — premium metal samples, stems and multi-tracks for
                    producers and musicians.
                  </p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2">
                    {product.tags.map((tag) => (
                      <span
                        key={tag}
                        className="font-mono text-xs text-white/15 border border-white/10 px-2 py-0.5 tracking-wide blur-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Price + CTA */}
                  <div className="mt-auto pt-4 border-t border-white/5 flex items-center justify-between gap-4">
                    <div>
                      <span className="font-display font-extrabold text-2xl text-white/20 blur-sm">
                        £XX
                      </span>
                    </div>
                    <span className="font-mono text-xs px-5 py-2 border border-white/10 text-white/25 whitespace-nowrap cursor-not-allowed">
                      Coming Soon
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Bottom CTA */}
          <div className="reveal mt-16 border border-primary/20 p-10 text-center relative overflow-hidden">
            {/* Background image */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: "url('/assets/images/mixing-1.jpg')",
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              }}
            />
            {/* Dark overlay */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'rgba(0,0,0,0.80)' }}
            />
            {/* Glow */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  'radial-gradient(ellipse at center, rgba(204,0,0,0.08) 0%, transparent 70%)',
              }}
            />
            <div className="relative z-10">
              <span className="section-label block mb-3">// Drops Incoming</span>
              <h3 className="font-display font-bold text-2xl uppercase tracking-tight text-fg-base mb-3">
                Be First to Know
              </h3>
              <p className="font-body text-muted text-base max-w-md mx-auto mb-6">
                Sample packs, stems, multi-tracks and sheet music are on the way. Get in touch to be
                notified the moment they drop.
              </p>
              <Link
                href="/contact"
                className="btn-primary px-8 py-3 text-sm inline-flex items-center gap-2"
              >
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
